import { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { CheckCircle2, Download, Loader2, ArrowLeft, XCircle } from "lucide-react";
import { api, API } from "@/lib/api";
import { Eyebrow } from "@/components/Reveal";
import Seo from "@/components/Seo";

export const PaymentSuccess = () => {
  const [params] = useSearchParams();
  const sessionId = params.get("session_id");
  const [state, setState] = useState({ phase: "polling", downloads: [] });

  useEffect(() => {
    if (!sessionId) {
      setState({ phase: "error", downloads: [] });
      return;
    }
    let attempts = 0;
    let cancelled = false;
    const poll = async () => {
      try {
        const { data } = await api.get(`/payments/status/${sessionId}`);
        if (data.payment_status === "paid") {
          const dl = await api.get(`/downloads/${sessionId}`);
          if (!cancelled) setState({ phase: "paid", downloads: dl.data.downloads });
          return;
        }
      } catch {
        // keep polling briefly
      }
      attempts += 1;
      if (!cancelled && attempts < 15) setTimeout(poll, 2000);
      else if (!cancelled) setState({ phase: "timeout", downloads: [] });
    };
    poll();
    return () => {
      cancelled = true;
    };
  }, [sessionId]);

  return (
    <div className="py-24 lg:py-32" data-testid="payment-success-page">
      <Seo title="Order Confirmed" noindex />
      <div className="max-w-2xl mx-auto px-4 text-center">
        {state.phase === "polling" && (
          <>
            <Loader2 className="w-12 h-12 text-teal animate-spin mx-auto" />
            <h1 className="mt-6 font-serif text-3xl text-teal">Confirming your payment…</h1>
            <p className="mt-3 text-charcoal/70">This takes a few seconds.</p>
          </>
        )}
        {state.phase === "paid" && (
          <>
            <CheckCircle2 className="w-14 h-14 text-seaglass mx-auto" />
            <Eyebrow>Payment Confirmed</Eyebrow>
            <h1 className="mt-4 font-serif text-3xl sm:text-4xl text-teal tracking-tight">
              Your toolkits are ready.
            </h1>
            <p className="mt-4 text-charcoal/70 leading-relaxed">
              Download your PDFs below. Save them somewhere safe — and keep an eye on your inbox
              for your receipt.
            </p>
            <div className="mt-8 space-y-3 text-left">
              {state.downloads.map((d) => (
                <a
                  key={d.product_id}
                  href={`${API}${d.url.replace("/api", "")}`}
                  data-testid={`stripe-download-link-${d.product_id}`}
                  className="flex items-center justify-between gap-4 bg-white border border-cloud px-6 py-4 hover:border-teal transition-colors"
                >
                  <span className="font-serif text-lg text-teal">{d.title}</span>
                  <span className="inline-flex items-center gap-2 text-xs font-mono uppercase tracking-[0.14em] text-teal shrink-0">
                    <Download className="w-4 h-4" /> PDF
                  </span>
                </a>
              ))}
            </div>
            <Link
              to="/hub"
              className="mt-10 inline-flex items-center gap-2 bg-teal text-ivory font-medium px-7 py-3.5 rounded-full hover:bg-teal-dark transition-colors"
            >
              Keep learning in the Hub
            </Link>
          </>
        )}
        {(state.phase === "error" || state.phase === "timeout") && (
          <>
            <XCircle className="w-12 h-12 text-charcoal/40 mx-auto" />
            <h1 className="mt-6 font-serif text-3xl text-teal">
              We couldn't confirm that payment just yet
            </h1>
            <p className="mt-3 text-charcoal/70">
              If your card was charged, your downloads will be available shortly — please contact
              us with your receipt and we'll sort it out immediately.
            </p>
            <Link to="/contact" className="mt-8 inline-flex items-center gap-2 text-teal underline underline-offset-4">
              Contact support
            </Link>
          </>
        )}
      </div>
    </div>
  );
};

export const PaymentCancel = () => (
  <div className="py-24 lg:py-32" data-testid="payment-cancel-page">
    <Seo title="Checkout Cancelled" noindex />
    <div className="max-w-xl mx-auto px-4 text-center">
      <XCircle className="w-12 h-12 text-charcoal/40 mx-auto" />
      <h1 className="mt-6 font-serif text-3xl text-teal">Checkout cancelled</h1>
      <p className="mt-3 text-charcoal/70">
        No charge was made. Your bundle is still waiting whenever you're ready.
      </p>
      <Link
        to="/toolkits"
        className="mt-8 inline-flex items-center gap-2 bg-teal text-ivory font-medium px-7 py-3.5 rounded-full hover:bg-teal-dark transition-colors"
      >
        <ArrowLeft className="w-4 h-4" /> Back to the store
      </Link>
    </div>
  </div>
);
